import React from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList, faChartLine, faComments } from "@fortawesome/free-solid-svg-icons";
import {
    faQuoteLeft,
    faEnvelope,
    faPhone,
    faQuestionCircle,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb } from "../components/Breadcrumb";
import ReadyToPlan from "../components/ReadyToPlan";

const testimonials = [
    {
        name: "Richa S.",
        title: "IT Professional, Bangalore",
        heading: "From Confusion to Confidence — A Journey Worth Sharing",
        text: "Before I met SAG Financials, I was overwhelmed by the choices in mutual funds and SIPs. Shepra and her team patiently explained everything, broke down complex terms into simple concepts, and tailored an investment plan that matched my goals. Now, I regularly track my portfolio and feel confident about my financial future. It’s been a transformative experience.",
    },
    {
        name: "Vikas D.",
        title: "Small Business Owner, Pune",
        heading: "Insurance Made Simple and Effective",
        text: "Running a business keeps me busy, and insurance was something I kept postponing. SAG Financials assessed my personal and business risks and recommended comprehensive protection plans that fit my budget. Their clear explanations helped me choose the right term insurance and health cover without confusion. It’s peace of mind that’s invaluable.",
    },
    {
        name: "Anita K.",
        title: "Homemaker & Aspiring Entrepreneur, Delhi",
        heading: "Financial Planning That Respects My Goals and Timeline",
        text: "I wanted to save for my children’s education and also start a small business but wasn’t sure how to balance both. SAG Financials helped me create a realistic plan aligned with my income and priorities. They never rushed me or pushed products — just honest advice and support. Their digital platform makes managing investments easy.",
    },
    {
        name: "Ramesh P.",
        title: "Chartered Accountant, Mumbai",
        heading: "Expert Tax Planning Helped Me Save Thousands",
        text: "As a finance professional, I understand taxes well, but SAG Financials brought fresh perspectives that optimized my savings. Their strategic advice on deductions, ELSS, and tax-efficient investments was practical and effective. Highly recommended for anyone wanting a professional yet personal advisory service.",
    },
    {
        name: "Sunita M.",
        title: "Senior Manager, Hyderabad",
        heading: "Retirement Planning That Feels Secure and Realistic",
        text: "Planning for retirement felt overwhelming. SAG Financials guided me through creating a goal-based retirement plan suited to my lifestyle and risk appetite. Their team explained how to balance safe investments with growth options, and their tracking tools keep me updated regularly. I now look forward to retirement with confidence.",
    },
    {
        name: "Nikhil T.",
        title: "Freelance Graphic Designer, Chennai",
        heading: "Financial Clarity for Freelancers and Consultants",
        text: "Freelancing means irregular income, which made planning tough. SAG Financials customized a flexible strategy that accounted for my fluctuating earnings while helping me save and invest wisely. Their guidance on insurance and emergency funds was eye-opening. I finally feel in control, despite freelance uncertainty.",
    },
];

const Testimonial = () => {
    return (
        <>
            <Breadcrumb currentName={"Testimonials"} currentParentName={"Testimonials"} />
            {/* Intro */}
            <div className="container py-5">
                <div className="text-center mb-5">
                    <h2 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>
                        Real Stories. Real Success. Real People.
                    </h2>
                    <p className="mx-auto" style={{ maxWidth: "800px", color: 'var(--text)' }}>
                        At <strong style={{ color: 'var(--primary)' }}>SAG Financials</strong>, our greatest pride comes from
                        the trust and satisfaction of our clients. Every individual, family,
                        and business we partner with has a unique story — and it's their
                        success that motivates us every day.
                    </p>
                    <p className="mx-auto" style={{ maxWidth: "800px", color: 'var(--text)' }}>
                        Listening to real experiences is the best way to understand what we
                        stand for. Below are genuine testimonials from clients who have
                        transformed their financial lives through personalized planning and
                        expert guidance.
                    </p>
                </div>
            </div>

            {/* Why Clients Trust Us */}
            <div className="container-fluid py-5" style={{ backgroundColor: 'var(--light-bg)' }}>
                <div className="container text-center">
                    <h3 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>Why Our Clients Trust Us</h3>
                    <p className="mx-auto" style={{ maxWidth: "700px", color: 'var(--text)' }}>
                        With over 22 years of experience and certifications from AMFI, IRDA,
                        and NISM, SAG Financials has helped hundreds achieve financial goals
                        with clarity and confidence. We don't just sell products — we build
                        transparent, educational, long-lasting relationships.
                    </p>
                </div>
            </div>

            {/* Testimonials */}
            <div className="container py-5">
                <div className="text-center mb-4">
                    <h2 className="fw-bold" style={{ color: 'var(--heading)' }}>What Our Clients Are Saying</h2>
                </div>
                <div className="row g-4">
                    {testimonials.map((t, i) => (
                        <motion.div
                            key={i}
                            className="col-md-6 col-lg-4"
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                        >
                            <div className="border rounded p-4 h-100 shadow-sm" style={{ backgroundColor: 'var(--white)' }}>
                                <FontAwesomeIcon
                                    icon={faQuoteLeft}
                                    style={{ color: 'var(--primary)' }}
                                    className="fs-3 mb-3"
                                />
                                <h5 className="fw-bold mb-2" style={{ color: 'var(--heading)' }}>{t.heading}</h5>
                                <p style={{ color: 'var(--text)' }}>{t.text}</p>
                                <hr />
                                <p className="fw-semibold mb-0" style={{ color: 'var(--heading)' }}>{t.name}</p>
                                <small style={{ color: 'var(--text)' }}>{t.title}</small>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Commitment */}
            {/* Our Commitment to You */}
            <div className="container-fluid py-5" style={{ backgroundColor: 'var(--light-bg)' }}>
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>Our Commitment to You</h2>
                        <p className="mx-auto" style={{ maxWidth: "700px", color: 'var(--text)' }}>
                            Every testimonial reflects what truly defines <strong style={{ color: 'var(--primary)' }}>SAG Financials</strong> —
                            a team dedicated to empowering you with trust, clarity, and long-term success.
                        </p>
                    </div>

                    <div className="row g-4">
                        {[
                            {
                                title: "We Listen First",
                                text: "Your goals, fears, and dreams come first. Every plan begins with understanding you.",
                                icon: faComments,
                            },
                            {
                                title: "We Educate",
                                text: "No confusing jargon — we simplify finance and make every decision easy to understand.",
                                icon: faCheckCircle,
                            },
                            {
                                title: "We Personalize",
                                text: "Your financial roadmap is as unique as your life, crafted with precision and empathy.",
                                icon: faClipboardList,
                            },
                            {
                                title: "We Support",
                                text: "We stand with you at every milestone, offering ongoing reviews, calls, and updates.",
                                icon: faPhone,
                            },
                            {
                                title: "We Deliver Results",
                                text: "Our goal is simple — measurable progress and real financial confidence.",
                                icon: faChartLine,
                            },
                        ].map((c, i) => (
                            <motion.div
                                key={i}
                                className="col-md-6 col-lg-4"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                            >
                                <div className="rounded p-4 shadow-sm h-100 text-center commitment-card" style={{ backgroundColor: 'var(--white)' }}>
                                    <div className="icon-circle mb-3 mx-auto" style={{ backgroundColor: 'var(--light-bg)' }}>
                                        <FontAwesomeIcon
                                            icon={c.icon}
                                            style={{ color: 'var(--primary)' }}
                                            className="fs-3"
                                        />
                                    </div>
                                    <h5 className="fw-bold mb-2" style={{ color: 'var(--heading)' }}>{c.title}</h5>
                                    <p style={{ color: 'var(--text)' }}>{c.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>


            {/* Share Your Story */}
            <div className="container py-5 text-center">
                <h2 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>Share Your Story</h2>
                <p className="mx-auto" style={{ maxWidth: "700px", color: 'var(--text)' }}>
                    Have you worked with <strong style={{ color: 'var(--primary)' }}>SAG Financials</strong>? We'd love to
                    hear from you! Your story could inspire others to take control of
                    their finances.
                </p>
                <p className="fs-5" style={{ color: 'var(--text)' }}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ color: 'var(--primary)' }} className="me-2" />
                    <strong>Email:</strong> contact@sagfinancials.com  &nbsp; | &nbsp;
                    <FontAwesomeIcon icon={faPhone} style={{ color: 'var(--primary)' }} className="me-2" />
                    <strong>WhatsApp:</strong> +917011468336
                </p>
            </div>

            {/* FAQ */}
            <div className="container-fluid py-5" style={{ backgroundColor: 'var(--light-bg)' }}>
                <div className="container">
                    <div className="text-center mb-4">
                        <h2 className="fw-bold" style={{ color: 'var(--heading)' }}>Frequently Asked Questions</h2>
                    </div>
                    <div className="accordion" id="faqAccordion">
                        {[
                            {
                                q: "How soon can I expect results from financial planning?",
                                a: "Results vary by individual goals, but many clients see improved clarity and measurable progress within the first few months.",
                            },
                            {
                                q: "Is your advice unbiased and product-agnostic?",
                                a: "Absolutely. We recommend only what suits your goals — not what earns us commissions.",
                            },
                            {
                                q: "Do you offer ongoing support?",
                                a: "Yes. We provide continuous monitoring, reviews, and plan updates as your life evolves.",
                            },
                        ].map((f, i) => (
                            <motion.div
                                key={i}
                                className="accordion-item mb-3"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.1 }}
                            >
                                <h2 className="accordion-header" id={`heading${i}`}>
                                    <button
                                        className={`accordion-button ${i !== 0 ? "collapsed" : ""}`}
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target={`#collapse${i}`}
                                        aria-expanded={i === 0 ? "true" : "false"}
                                        aria-controls={`collapse${i}`}
                                        style={{ backgroundColor: 'var(--white)', color: 'var(--heading)' }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faQuestionCircle}
                                            style={{ color: 'var(--primary)' }}
                                            className="me-2"
                                        />
                                        {f.q}
                                    </button>
                                </h2>
                                <div
                                    id={`collapse${i}`}
                                    className={`accordion-collapse collapse ${i === 0 ? "show" : ""
                                        }`}
                                    aria-labelledby={`heading${i}`}
                                    data-bs-parent="#faqAccordion"
                                >
                                    <div className="accordion-body" style={{ color: 'var(--text)', backgroundColor: 'var(--white)' }}>{f.a}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Closing */}
            <div className="container py-5 text-center">
                <h2 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>Why Clients Trust SAG Financials</h2>
                <p className="mx-auto" style={{ maxWidth: "700px", color: 'var(--text)' }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--primary)' }} className="me-2" />
                    22+ Years of Experience | Certified & Ethical | Client-First Approach |
                    Award-Winning Track Record | 100% Digital, 100% Human
                </p>
            </div>

           <ReadyToPlan/>
        </>
    );
};

export default Testimonial;
